/**
 * A thumb up or down after dinner (#1284).
 *
 * ⛔ THIS DOES NOT CHANGE ANYBODY'S PROFILE, and the copy says so. It records
 * what happened at one meal. What somebody SAID about a food stays where they
 * put it, and only they move it — if one bad salmon on a Tuesday quietly
 * rewrote "I love fish", the app would stop offering fish and nobody would
 * know why.
 *
 * ⚠️ One tap per person, on the plan screen, the following morning. Anything
 * longer than that does not get done, and a rating nobody gives is worse than
 * no feature at all.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { twoPlates, type Eater } from "../api/twoPlates";

const CHOICES = [
  { rating: 1, label: "Good", hint: "cook it again" },
  { rating: 0, label: "Fine", hint: "no strong feeling" },
  { rating: -1, label: "No", hint: "not again soon" },
];

export function HowWasIt({
  slotId,
  mealName,
  eaters,
}: {
  slotId: number;
  mealName: string;
  eaters: Eater[];
}) {
  const queryClient = useQueryClient();
  const [ratings, setRatings] = useState<Record<number, number>>({});
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      twoPlates.logCooked(
        slotId,
        Object.entries(ratings).map(([eaterId, rating]) => ({ eaterId: Number(eaterId), rating })),
      ),
    onSuccess: () => {
      setDone(true);
      // The deck reads this, so it should re-rank straight away.
      queryClient.invalidateQueries({ queryKey: ["deck"] });
    },
  });

  if (done) {
    return (
      <p className="text-muted mt-3 text-xs">
        Noted. It will show up in how the deck orders things — nobody's answers changed.
      </p>
    );
  }

  const answered = Object.keys(ratings).length;

  return (
    <div className="border-line mt-3 rounded-lg border p-3">
      <p className="text-muted text-xs uppercase tracking-wide">How was {mealName}?</p>

      <div className="mt-3 flex flex-col gap-2.5">
        {eaters.map((eater, i) => (
          <div key={eater.id} className="flex items-center gap-2">
            <span
              aria-hidden
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: i % 2 === 0 ? "var(--color-plate-a)" : "var(--color-plate-b)" }}
            />
            <span className="w-16 shrink-0 truncate text-sm">{eater.name}</span>
            <div className="flex flex-1 gap-1.5">
              {CHOICES.map((c) => {
                const chosen = ratings[eater.id] === c.rating;
                return (
                  <button
                    key={c.rating}
                    onClick={() => setRatings((prev) => ({ ...prev, [eater.id]: c.rating }))}
                    aria-pressed={chosen}
                    title={c.hint}
                    className="border-line flex-1 rounded-lg border py-2 text-xs font-medium"
                    style={
                      chosen
                        ? {
                            borderColor: c.rating === -1 ? "var(--color-warn)" : "var(--color-plate-b)",
                            color: c.rating === -1 ? "var(--color-warn)" : "var(--color-plate-b)",
                          }
                        : undefined
                    }
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={answered === 0 || save.isPending}
          className="bg-plate-b text-ground rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-40"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button onClick={() => setDone(true)} className="text-muted text-sm underline underline-offset-4">
          Skip
        </button>
      </div>

      {/* Said out loud, because the distinction is the whole design. */}
      <p className="text-muted mt-2 text-[11px]">
        This is about the meal, not about anybody's tastes. Nothing you say here
        changes what you told us you eat.
      </p>

      {save.isError && (
        <p role="alert" className="text-warn mt-2 text-sm">That did not save. Try again.</p>
      )}
    </div>
  );
}
